import type {BarChartType} from "@mantine/charts";
import {Popover, Progress, Tooltip} from "@mantine/core";
import {DateTime} from "luxon";
import React, {useRef} from "react";
import {ArrowDownUp, SortDownAlt, SortUp, Filter} from "react-bootstrap-icons";
import {twMerge} from "tailwind-merge";
import {stringify as csvStringify} from "csv-stringify/browser/esm/sync";
import {
    newReferenceFontSize,
    referenceElementWidth,
    referenceFontSize,
    widgetBottomBarHeightInRem,
    widgetTopBarHeightInRem,
} from "~/components/reports/reportConstants";
import {ViewWidgetPopover} from "~/components/reports/viewWidgetPopover";
import type {MultiBarGraphWidget} from "~/components/reports/widgetSpecific/barGraph";
import type {HorizontalBarGraphWidget} from "~/components/reports/widgetSpecific/horizontalBarGraph";
import type {StackedBarGraphWidget} from "~/components/reports/widgetSpecific/stackedBarGraph";
import type {TableWidget} from "~/components/reports/widgetSpecific/table";
import type {EditableReportTableData} from "~/components/reports/widgetSpecific/types";
import type {Widget} from "~/components/reports/widgetSpecific/widgets";
import {WidgetLoadingState} from "~/components/reports/widgetSpecific/widgets";
import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {AreaChart} from "~/components/reports/widgets/areaChart";
import {BarChart} from "~/components/reports/widgets/barChart";
import {ComboChart} from "~/components/reports/widgets/comboChart";
import {DonutChart} from "~/components/reports/widgets/donutChart";
import {Gauge} from "~/components/reports/widgets/gauge";
import {HorizontalBarChart} from "~/components/reports/widgets/horizontalBarChart";
import {LineChart} from "~/components/reports/widgets/lineChart";
import {PieChartCustom} from "~/components/reports/widgets/pieChartCustom";
import {StackedBarChart} from "~/components/reports/widgets/stackedBarChart";
import {
    type ColumnProperties,
    ColumnSortType,
    QueryResolverErrorCodes,
    TableColumnTextOverflow,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-types";
import {
    filterDataPoint,
    sortDataPoints,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-utilities";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import BarChartIncomplete from "~/svgs/barChartIncomplete.svg?react";
import LineChartIncomplete from "~/svgs/lineChartIncomplete.svg?react";
import NumberTileIncomplete from "~/svgs/numberTileIncomplete.svg?react";
import PieChartIncomplete from "~/svgs/pieChartIncomplete.svg?react";
import ProgressBarIncomplete from "~/svgs/progressBarIncomplete.svg?react";
import TableIncomplete from "~/svgs/tableIncomplete.svg?react";
import {IntegrationTypeDisplayMetadata} from "~/utilities/typeDefinitions";
import {numberToHumanFriendlyString, numberToHumanFriendlyStringWithNumberSystem, showInfoToast, showSuccessToast} from "~/utilities/utilities";
import {NumberSystems} from "~/utilities/typeDefinitions";

const paddingInRem = 0.5;
const remToPx = 16;

export function BarGraphWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
    barGraphType,
}: {
    widget: MultiBarGraphWidget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
    barGraphType: BarChartType;
}) {
    if (widget.type !== WidgetType.multiBarGraph) {
        throw new Error("733c4709-7572-4a1b-862d-5895aee59e49");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data !== null &&
            widget.data.chartData !== null &&
            widget.data.chartSeries !== null &&
            widget.xAxisKey !== null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2">
                    <BarChart
                        chartData={widget.data.chartData.filter((dataPoint) => filterDataPoint(widget, dataPoint))}
                        xAxisKey={widget.xAxisKey}
                        chartSeries={widget.data.chartSeries}
                        height={
                            widgetHeight -
                            widgetTopBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            paddingInRem * remToPx
                        }
                        maxYValue={widget.data.maxYValue}
                        maxYValueSecondary={widget.data.maxYValueSecondary}
                        scalingFactor={scalingFactor}
                        xAxisTitle={widget.xAxisKey}
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <BarChartIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteBarGraphWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <BarChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

export function StackedBarGraphWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
    barGraphType,
}: {
    widget: StackedBarGraphWidget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
    barGraphType: BarChartType;
}) {
    if (widget.type !== WidgetType.stackedBarGraph) {
        throw new Error("3552bbcc-9193-4118-a68c-129a1726e757");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data !== null &&
            widget.data.chartData !== null &&
            widget.data.chartSeries !== null &&
            widget.data.chartSeries.length > 0 &&
            widget.xAxisKey !== null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2">
                    <StackedBarChart
                        chartData={widget.data.chartData}
                        chartSeries={widget.data.chartSeries}
                        maxYValue={widget.data.maxYValue}
                        maxYValueSecondary={widget.data.maxYValueSecondary}
                        scalingFactor={scalingFactor}
                        xAxisTitle={widget.xAxisKey}
                        xAxisKey={widget.xAxisKey}
                        height={
                            widgetHeight -
                            widgetTopBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            paddingInRem * remToPx
                        }
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <BarChartIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteStackedBarGraphWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <BarChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

export function HorizontalBarGraphWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
    barGraphType,
}: {
    widget: HorizontalBarGraphWidget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
    barGraphType: BarChartType;
}) {
    if (widget.type !== WidgetType.horizontalBarGraph) {
        throw new Error("926917fb-b74f-4d47-aa2a-21c49890a5d2");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data !== null &&
            widget.data.chartData !== null &&
            widget.data.chartSeries !== null &&
            widget.xAxisKey !== null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2">
                    <HorizontalBarChart
                        chartData={widget.data.chartData.filter((dataPoint) => filterDataPoint(widget, dataPoint))}
                        xAxisKey={widget.xAxisKey}
                        chartSeries={widget.data.chartSeries}
                        height={
                            widgetHeight -
                            widgetTopBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            paddingInRem * remToPx
                        }
                        maxYValue={widget.data.maxYValue}
                        maxYValueSecondary={widget.data.maxYValueSecondary}
                        scalingFactor={scalingFactor}
                        xAxisTitle={widget.xAxisKey}
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <BarChartIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteHorizontalBarGraphWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <BarChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

function getNextActionableToCompleteBarGraphWidget(
    widget: Widget & {
        type: WidgetType.multiBarGraph;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.chartSeriesDetails.length < widget.minMetricSelections || widget.chartSeriesDetails.length > widget.maxMetricSelections
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections
            ? "Please select dimensions"
            : "Do something else";
}

function getNextActionableToCompleteHorizontalBarGraphWidget(
    widget: Widget & {
        type: WidgetType.horizontalBarGraph;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.chartSeriesDetails.length < widget.minMetricSelections || widget.chartSeriesDetails.length > widget.maxMetricSelections
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections
            ? "Please select dimensions"
            : "Do something else";
}

function getNextActionableToCompleteStackedBarGraphWidget(
    widget: Widget & {
        type: WidgetType.stackedBarGraph;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.selectedMetrics.length !== 1
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections
            ? "Please select dimensions"
            : widget.selectedSlicer === null
              ? "Please select a slicer to break down the metric"
              : "Do something else";
}

export function AreaGraphWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
}) {
    if (widget.type != WidgetType.areaGraph) {
        throw new Error("1d2c7236-e7e0-4b7f-a947-81d573316227");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                // height: "100%",
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data != null &&
            widget.data.chartData != null &&
            widget.data.chartSeries != null &&
            widget.xAxisKey != null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2">
                    <AreaChart
                        chartData={widget.data.chartData.filter((dataPoint) => filterDataPoint(widget, dataPoint))}
                        xAxisKey={widget.xAxisKey}
                        chartSeries={widget.data.chartSeries}
                        height={
                            widgetHeight -
                            widgetTopBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            paddingInRem * remToPx
                        }
                        xAxisTitle={widget.xAxisKey}
                        maxYValue={widget.data.maxYValue}
                        maxYValueSecondary={widget.data.maxYValueSecondary}
                        scalingFactor={scalingFactor}
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <BarChartIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteAreaGraphWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <BarChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

export function ComboChartWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
}) {
    if (widget.type !== WidgetType.comboChart) {
        throw new Error("693541f3-574a-468a-996c-296b0a034f6b");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                // height: "100%",
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data != null &&
            widget.data.chartData != null &&
            widget.data.chartSeries != null &&
            widget.xAxisKey != null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2">
                    <ComboChart
                        scalingFactor={scalingFactor}
                        chartData={widget.data.chartData}
                        chartSeries={widget.data.chartSeries}
                        xAxisKey={widget.xAxisKey}
                        height={
                            widgetHeight -
                            widgetTopBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            paddingInRem * remToPx
                        }
                        maxYValue={widget.data.maxYValue}
                        maxYValueSecondary={widget.data.maxYValueSecondary}
                        xAxisTitle={widget.xAxisKey}
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <BarChartIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteComboChartWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <BarChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

function getNextActionableToCompleteAreaGraphWidget(
    widget: Widget & {
        type: WidgetType.areaGraph;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.chartSeriesDetails.length < widget.minMetricSelections || widget.chartSeriesDetails.length > widget.maxMetricSelections
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections
            ? "Please select dimensions"
            : "Do something else";
}

export function LineGraphWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
}) {
    if (widget.type != WidgetType.lineGraph) {
        throw new Error("7a833357-25e2-4e38-9329-fdeddc22fbdf");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                // height: "100%",
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data != null &&
            widget.data.chartData != null &&
            widget.data.chartSeries != null &&
            widget.xAxisKey != null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2">
                    <LineChart
                        chartData={widget.data.chartData.filter((dataPoint) => filterDataPoint(widget, dataPoint))}
                        xAxisKey={widget.xAxisKey}
                        chartSeries={widget.data.chartSeries}
                        height={
                            widgetHeight -
                            widgetTopBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            widgetBottomBarHeightInRem * remToPx -
                            paddingInRem * remToPx
                        }
                        maxYValue={widget.data.maxYValue}
                        maxYValueSecondary={widget.data.maxYValueSecondary}
                        scalingFactor={scalingFactor}
                        xAxisTitle={widget.xAxisKey}
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <LineChartIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteLineGraphWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <LineChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

function getNextActionableToCompleteLineGraphWidget(
    widget: Widget & {
        type: WidgetType.lineGraph;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.chartSeriesDetails.length < widget.minMetricSelections || widget.chartSeriesDetails.length > widget.maxMetricSelections
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections
            ? "Please select dimensions"
            : "Do something else";
}

function getNextActionableToCompleteComboChartWidget(
    widget: Widget & {
        type: WidgetType.comboChart;
    },
) {
    const selectedMetricCount = widget.chartSeriesDetails.map((series) => series.selectedMetrics).flat().length;

    return widget.datasetId === null
        ? "Please select Data Cluster"
        : selectedMetricCount < widget.minMetricSelections || selectedMetricCount > widget.maxMetricSelections
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections
            ? "Please select dimensions"
            : "Do something else";
}

export function NumberTileWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    hideKebabMenu?: boolean;
}) {
    if (widget.type != WidgetType.numberTile) {
        throw new Error("984cd812-6ead-4f84-a64b-97d7400fd8a3");
    }

    const fontSizeMultiplier = Number.isNaN(parseFloat(widget.fontSizeMultiplier)) ? 1 : parseFloat(widget.fontSizeMultiplier);

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                // height: "100%",
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.aggregatedValue != null && widget.title != null && widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="row-start-2 grid grid-flow-row auto-rows-max content-center items-center">
                    {/* <div
                        className="text-center gj-text-caption-400 leading-[2.25] text-ds-text-subtle"
                        style={{
                            fontSize: `${
                                newReferenceFontSize *
                                2 *
                                scalingFactor *
                                fontSizeMultiplier
                            }px`,
                            lineHeight: 1.5,
                        }}
                    >
                        {widget.title}
                    </div> */}
                    <div
                        className="text-center text-ds-text"
                        style={{
                            fontSize: `${newReferenceFontSize * 5 * scalingFactor * fontSizeMultiplier}px`,
                            lineHeight: 1.5,
                        }}
                    >
                        {widget.valuePrefix}
                        {numberToHumanFriendlyStringWithNumberSystem(
                            widget.aggregatedValue,
                            widget.numberSystem ?? NumberSystems.international,
                            widget.decimalPlaces,
                            !widget.spreadValue,
                        )}
                        {widget.valueSuffix}
                    </div>
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <NumberTileIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteNumberTileWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <NumberTileIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ds-text self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}
                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

function getNextActionableToCompleteNumberTileWidget(
    widget: Widget & {
        type: WidgetType.numberTile;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.selectedMetrics.length < widget.minMetricSelections || widget.selectedMetrics.length > widget.maxMetricSelections
          ? "Please select metrics"
          : "Do something else";
}

export function PieChartWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
}) {
    if (widget.type != WidgetType.pieChart) {
        throw new Error("0c2503fd-13ee-481a-aa87-8049e637f86b");
    }

    const baseSize =
        widgetHeight - widgetTopBarHeightInRem * remToPx - widgetBottomBarHeightInRem * remToPx - widgetBottomBarHeightInRem * remToPx - paddingInRem * remToPx;

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data != null && widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-visible overflow-x-clip row-start-2">
                    <PieChartCustom
                        baseSize={baseSize}
                        data={widget.data.filter((dataPoint) => filterDataPoint(widget, dataPoint))}
                        title={widget.title}
                        valuesPrefix={widget.valuesPrefix}
                        scalingFactor={scalingFactor}
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <PieChartIncomplete className="w-full h-full " />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompletePieChartWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <PieChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

export function DonutChartWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    frontendFilters,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
}) {
    if (widget.type !== WidgetType.donutChart) {
        throw new Error("54785712-20e5-490e-8e03-cc9f3b5ee75b");
    }

    const baseSize =
        widgetHeight - widgetTopBarHeightInRem * remToPx - widgetBottomBarHeightInRem * remToPx - widgetBottomBarHeightInRem * remToPx - paddingInRem * remToPx;

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.data != null && widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row h-full overflow-y-visible items-start overflow-x-clip row-start-2">
                    <DonutChart
                        baseSize={baseSize}
                        data={widget.data.filter((dataPoint) => filterDataPoint(widget, dataPoint))}
                        title={widget.title}
                        valuesPrefix={widget.valuesPrefix}
                        scalingFactor={scalingFactor}
                    />
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <PieChartIncomplete className="w-full h-full " />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompletePieChartWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <PieChartIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover clickOutsideEvents={["mouseup"]}>
                            <Popover.Target>
                                <div>
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

function getNextActionableToCompletePieChartWidget(
    widget: Widget & {
        type: WidgetType.pieChart;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.selectedMetrics.length < widget.minMetricSelections || widget.selectedMetrics.length > widget.maxMetricSelections
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections
            ? "Please select dimensions"
            : "Do something else";
}

export function TextWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    onFilterButtonClick,
    onFilterSelect,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    onFilterSelect: (items: Array<string>, key: string) => void;
    datePicker: React.ReactNode;
    onFilterButtonClick: () => void;
}) {
    if (widget.type != WidgetType.text) {
        throw new Error("c000dc32-b635-4198-8213-adba578d0503");
    }

    return (
        <div
            className={twMerge(
                "p-4 w-full h-full grid grid-cols-1 grid-rows-1",
                widget.textHorizontalAlign == "left"
                    ? "text-left justify-items-start"
                    : widget.textHorizontalAlign == "right"
                      ? "text-right justify-items-end"
                      : "text-center justify-items-center",
                widget.textVerticalAlign == "top" ? "items-start" : widget.textVerticalAlign == "bottom" ? "items-end" : "items-center",
                className,
            )}
            style={{
                fontSize: `${widget.fontSizeMultiplier}rem`,
            }}
        >
            {widget.text}
        </div>
    );
}

// function getNextActionableToCompleteTextWidget(widget: Widget & {
//     type: WidgetType.text,
// }) {
//         return (widget.datasetId === null ? "Please select Data Cluster" : widget.selectedMetrics.length < widget.minMetricSelections || widget.selectedMetrics.length > widget.maxMetricSelections ? "Please select metrics" : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections ? "Please select dimensions" : "Do something else");
// }

export function ImageWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    onFilterButtonClick,
    onFilterSelect,
}: {
    widget: Widget;
    widgetWidth: number;
    widgetHeight: number;
    className?: string;
    scalingFactor: number;
    onFilterSelect: (items: Array<string>, key: string) => void;
    datePicker: React.ReactNode;
    onFilterButtonClick: () => void;
}) {
    if (widget.type != WidgetType.image) {
        throw new Error("a899fbaf-96ab-48e0-a237-e5ac14de4dee");
    }

    return <div className={twMerge("p-4", className)}>Image</div>;
}

// function getNextActionableToCompleteImageWidget(widget: Widget & {
//     type: WidgetType.image,
// }) {
//         return (widget.datasetId === null ? "Please select Data Cluster" : widget.selectedMetrics.length < widget.minMetricSelections || widget.selectedMetrics.length > widget.maxMetricSelections ? "Please select metrics" : widget.selectedDimensions.length < widget.minDimensionSelections || widget.selectedDimensions.length > widget.maxDimensionSelections ? "Please select dimensions" : "Do something else");
// }

function getTransposedTableColumnName(widget: TableWidget): string | null {
    if (widget.transposeDimensionId === null || widget.transposeDimensionId === undefined || widget.columnAttributeIds === null) {
        return null;
    }

    if (widget.selectedDimensions.length !== 1 || widget.selectedDimensions[0] !== widget.transposeDimensionId || widget.selectedMetrics.length === 0) {
        return null;
    }

    return Object.entries(widget.columnAttributeIds).find(([, attributeId]) => attributeId === widget.transposeDimensionId)?.[0] ?? null;
}

function formatTransposedTableColumnLabel(value: string | number | null, dataType: string | undefined): string {
    if (value === null) {
        return "-";
    }

    if (dataType === "date") {
        return DateTime.fromFormat(String(value), "dd LLL yyyy").toFormat("dd/MM/yyyy");
    }

    return String(value);
}

function getTableColumnKeys(widget: TableWidget): Array<string> {
    if (widget.columnMetadata === null) {
        return [];
    }

    const metadataKeys = Object.keys(widget.columnMetadata);
    if (widget.columnAttributeIds === null) {
        return metadataKeys;
    }

    const columnNameByAttributeId = new Map(
        Object.entries(widget.columnAttributeIds).map(([columnName, attributeId]) => [attributeId, columnName]),
    );
    const orderedKeys: Array<string> = [];

    for (const attributeId of [...widget.selectedDimensions, ...widget.selectedMetrics]) {
        const columnName = columnNameByAttributeId.get(attributeId);
        if (columnName !== undefined && columnName in widget.columnMetadata && orderedKeys.includes(columnName) === false) {
            orderedKeys.push(columnName);
        }
    }

    for (const columnName of metadataKeys) {
        if (orderedKeys.includes(columnName) === false) {
            orderedKeys.push(columnName);
        }
    }

    return orderedKeys;
}

function TransposedTableView({
    widget,
    data,
}: {
    widget: TableWidget;
    data: EditableReportTableData | null;
}) {
    if (data === null || data.length === 0) {
        return <div className="h-full w-full grid place-items-center text-ds-text-subtle">No Data Found!</div>;
    }

    const transposeColumnName = getTransposedTableColumnName(widget);
    if (transposeColumnName === null) {
        return null;
    }

    const rows = data
        .filter((dataPoint) => filterDataPoint(widget, dataPoint))
        .sort((dataPoint1, dataPoint2) => sortDataPoints(widget, widget.columnsToSort ?? {}, dataPoint1, dataPoint2));

    if (rows.length === 0) {
        return <div className="h-full w-full grid place-items-center text-ds-text-subtle">No Data Found!</div>;
    }

    const dimensionNames = new Set(
        Object.entries(widget.columnAttributeIds ?? {})
            .filter(([, attributeId]) => widget.selectedDimensions.includes(attributeId as Uuid))
            .map(([columnName]) => columnName),
    );

    const metricNames = Object.keys(rows[0] ?? {}).filter((columnName) => dimensionNames.has(columnName) === false);
    const transposeColumnType = widget.columnMetadata?.[transposeColumnName];
    const transposeColumnTitle = widget.columnProperties[transposeColumnName]?.columnTitle ?? transposeColumnName;
    const gridTemplateColumns = `minmax(13rem, 1fr) repeat(${rows.length}, minmax(7rem, 1fr))`;

    return (
        <div className="overflow-auto max-h-fit">
            <div
                className="grid grid-flow-row auto-rows-max min-w-full"
                style={{gridTemplateColumns}}
            >
                <div className="sticky top-0 left-0 z-20 bg-ds-surface font-semibold border border-ds-border px-3 py-[0.625rem]">
                    {transposeColumnTitle}
                </div>

                {rows.map((row, rowIndex) => (
                    <div
                        className="sticky top-0 z-10 bg-ds-surface font-semibold text-center border border-ds-border px-3 py-[0.625rem]"
                        key={`${transposeColumnName}-${rowIndex}`}
                    >
                        {formatTransposedTableColumnLabel(row[transposeColumnName] ?? null, transposeColumnType)}
                    </div>
                ))}

                {metricNames.map((metricName) => (
                    <React.Fragment key={metricName}>
                        <div className="sticky left-0 z-10 bg-ds-surface border border-ds-border px-3 py-[0.625rem] font-medium">
                            {widget.columnProperties[metricName]?.columnTitle ?? metricName}
                        </div>

                        {rows.map((row, rowIndex) => (
                            <div
                                className="overflow-hidden border border-ds-border px-3 py-[0.625rem] text-center"
                                key={`${metricName}-${rowIndex}`}
                            >
                                {row[metricName] ?? "-"}
                            </div>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

function RegularTableView({
    widget,
    data,
    keys,
    fontSize,
    tableGridTemplateColumns,
    tableRef,
    onSortSelect,
}: {
    widget: TableWidget;
    data: EditableReportTableData | null;
    keys: Array<string>;
    fontSize: number;
    tableGridTemplateColumns: string;
    tableRef?: React.RefObject<HTMLDivElement | null>;
    onSortSelect: (key: string) => void;
}) {
    if (data === null || data.length === 0) {
        return <div className="h-full w-full grid place-items-center text-ds-text-subtle">No Data Found!</div>;
    }

    const rows = data
        .filter((dataPoint) => filterDataPoint(widget, dataPoint))
        .sort((dataPoint1, dataPoint2) => sortDataPoints(widget, widget.columnsToSort ?? {}, dataPoint1, dataPoint2));

    if (rows.length === 0) {
        return <div className="h-full w-full grid place-items-center text-ds-text-subtle">No Data Found!</div>;
    }

    return (
        <div className="overflow-y-auto overflow-x-auto max-h-fit">
            <div
                className="grid grid-flow-row auto-rows-max row-start-2"
                ref={tableRef}
                style={{
                    gridTemplateColumns: tableGridTemplateColumns,
                    fontSize: `${fontSize > 100 ? 100 : fontSize}%`,
                }}
            >
                <div
                    className="row-start-1 col-span-full h-full w-full sticky top-0 left-0 right-0 grid grid-flow-col bg-ds-surface border-b-[thin] border-ds-border border-opacity-20"
                    style={{
                        gridTemplateColumns: tableGridTemplateColumns,
                    }}
                >
                    {keys.map((key, keyIndex) => {
                        return (
                            <div
                                className="overflow-hidden grid grid-flow-row auto-rows-max gap-y-1 border-t border-l border-r bg-ds-surface border-ds-border"
                                key={keyIndex}
                            >
                                <div className="w-full h-full px-3 py-[0.625rem] grid grid-flow-col auto-cols-max justify-between items-center">
                                    <div>
                                        {widget.columnProperties == null
                                            ? key
                                            : widget.columnProperties[key] != null
                                              ? widget.columnProperties[key]?.columnTitle
                                              : key}
                                    </div>

                                    {widget.columnsToSort !== undefined &&
                                        widget.columnsToSort !== null &&
                                        Object.keys(widget.columnsToSort).includes(key) && (
                                            <div
                                                id="should-hide-in-download"
                                                className="grid grid-flow-col auto-cols-max gap-x-2"
                                            >
                                                {widget.columnsToSort[key] == null ? (
                                                    <ArrowDownUp
                                                        size={16}
                                                        onClick={() => onSortSelect(key)}
                                                        className={twMerge(
                                                            "cursor-pointer transition-colors duration-100",
                                                            widget.columnsToSort[key] === ColumnSortType.descending
                                                                ? "text-white"
                                                                : "text-gray-500",
                                                        )}
                                                    />
                                                ) : widget.columnsToSort[key] === ColumnSortType.descending ? (
                                                    <SortUp
                                                        size={16}
                                                        onClick={() => onSortSelect(key)}
                                                        className={twMerge(
                                                            "cursor-pointer transition-colors duration-100",
                                                            widget.columnsToSort[key] === ColumnSortType.descending
                                                                ? "text-ds-text"
                                                                : "text-ds-text-subtlest",
                                                        )}
                                                    />
                                                ) : (
                                                    widget.columnsToSort[key] === ColumnSortType.ascending && (
                                                        <SortDownAlt
                                                            size={16}
                                                            onClick={() => onSortSelect(key)}
                                                            className={twMerge(
                                                                "cursor-pointer transition-colors duration-100",
                                                                widget.columnsToSort[key] === ColumnSortType.ascending
                                                                    ? "text-white"
                                                                    : "text-gray-500",
                                                            )}
                                                        />
                                                    )
                                                )}
                                            </div>
                                        )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {rows.map((row, rowIndex) => {
                    return keys.map((key, keyIndex) => (
                        <div
                            className="overflow-hidden px-3 py-[0.625rem] border border-ds-border grid place-items-center"
                            key={`${rowIndex}${keyIndex}`}
                        >
                            <div
                                style={{
                                    overflowWrap:
                                        widget.columnProperties == null
                                            ? "normal"
                                            : widget.columnProperties[key]?.columnTextOverflow === TableColumnTextOverflow.wrap
                                              ? "anywhere"
                                              : "normal",
                                }}
                                className="w-full grid items-center"
                            >
                                {row[key]}
                            </div>
                        </div>
                    ));
                })}

                <div className="col-span-full h-px w-full border-b-[thin] opacity-20" />
            </div>
        </div>
    );
}

export function TableWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    comparisonDatePicker,
    comparisonDateRangePending,
    onSortSelect,
    frontendFilters,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    datePicker: React.ReactNode;
    comparisonDatePicker?: React.ReactNode;
    comparisonDateRangePending?: boolean;
    onSortSelect: (key: string) => void;
    frontendFilters?: React.ReactNode;
    hideKebabMenu?: boolean;
}) {
    if (widget.type != WidgetType.table) {
        throw new Error("6b33d6e8-a72a-4c7e-b754-ec57c2399faf");
    }

    let keys: Array<string> | null = null;
    let fontSize = null;

    if (widget.data !== null && widget.columnMetadata !== null && widget.loadingState === WidgetLoadingState.loadedSuccessfully) {
        keys = getTableColumnKeys(widget);
        if (keys.length > 0) {
            fontSize = (widgetWidth / referenceElementWidth / keys.length) * 100;
        }
    }

    const tableRef = useRef<HTMLDivElement | null>(null);
    const tableGridTemplateColumns =
        keys === null
            ? undefined
            : getTableGridTemplateColumns({
                  keys,
                  columnProperties: widget.columnProperties,
                  widgetWidth,
              });

    const isComparisonEnabled = widget.comparison.enabled === true;
    const transposeColumnName = getTransposedTableColumnName(widget);
    const comparisonTableWidth = Math.max((widgetWidth - 2 * paddingInRem * remToPx - remToPx) / 2, 1);
    const comparisonTableGridTemplateColumns =
        keys === null
            ? undefined
            : getTableGridTemplateColumns({
                  keys,
                  columnProperties: widget.columnProperties,
                  widgetWidth: comparisonTableWidth,
                  horizontalPaddingInPx: 0,
              });
    const comparisonFontSize =
        keys === null || keys.length === 0
            ? null
            : (comparisonTableWidth / referenceElementWidth / keys.length) * 100;

    const primaryTableGridTemplateColumns = isComparisonEnabled ? comparisonTableGridTemplateColumns : tableGridTemplateColumns;
    const primaryFontSize = isComparisonEnabled ? comparisonFontSize : fontSize;

    const primaryTableView =
        widget.data != null && primaryFontSize != null && keys != null && primaryTableGridTemplateColumns != null && widget.loadingState === WidgetLoadingState.loadedSuccessfully
            ? transposeColumnName !== null
                ? <TransposedTableView widget={widget} data={widget.data} />
                : <RegularTableView
                      widget={widget}
                      data={widget.data}
                      keys={keys}
                      fontSize={primaryFontSize}
                      tableGridTemplateColumns={primaryTableGridTemplateColumns}
                      tableRef={tableRef}
                      onSortSelect={onSortSelect}
                  />
            : null;

    const comparisonTableView =
        comparisonDateRangePending !== true &&
        widget.comparisonLoadingState === WidgetLoadingState.loadedSuccessfully &&
        comparisonFontSize != null &&
        keys != null &&
        comparisonTableGridTemplateColumns != null
            ? transposeColumnName !== null
                ? <TransposedTableView widget={widget} data={widget.comparisonData} />
                : <RegularTableView
                      widget={widget}
                      data={widget.comparisonData}
                      keys={keys}
                      fontSize={comparisonFontSize}
                      tableGridTemplateColumns={comparisonTableGridTemplateColumns}
                      onSortSelect={onSortSelect}
                  />
            : null;

    return (
        <div
            className={twMerge("gap-y-2 bg-transparent", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
            }}
        >
            {primaryTableView != null ?
                isComparisonEnabled ? (
                    <div className="row-start-2 min-h-0 grid grid-cols-2 gap-4 overflow-auto">
                        <div className="min-w-0 flex min-h-0 flex-col gap-2">
                            {datePicker}
                            <div className="min-h-0 overflow-auto">{primaryTableView}</div>
                        </div>
                        <div className="min-w-0 flex min-h-0 flex-col gap-2">
                            {comparisonDatePicker}
                            <div className="min-h-0 overflow-auto">
                                {comparisonDateRangePending === true || widget.comparison.startDateIso === null || widget.comparison.endDateIso === null ? (
                                    <div className="h-full w-full grid place-items-center text-ds-text-subtle">Select both comparison dates</div>
                                ) : comparisonTableView != null ? (
                                    comparisonTableView
                                ) : widget.comparisonLoadingState === WidgetLoadingState.errorAfterLoading ? (
                                    <div className="h-full w-full grid place-items-center text-ds-text-subtle">
                                        {widget.comparisonErrorCode === QueryResolverErrorCodes.noDataFound
                                            ? "No comparison data found"
                                            : "Could not fetch comparison data"}
                                    </div>
                                ) : (
                                    <div className="h-full w-full grid place-items-center text-ds-text-subtle">Loading comparison data...</div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    primaryTableView
                )
            : widget.loadingState === WidgetLoadingState.incompleteWidget ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <TableIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteTableWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <TableIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {widget.filters != null && Object.entries(widget.filters).length > 0 && frontendFilters != null && (
                    <div className="cursor-pointer w-fit col-start-1">
                        <Popover
                            clickOutsideEvents={["mouseup"]}
                            position="bottom-start"
                        >
                            <Popover.Target>
                                <div className="p-2 hover:bg-ds-surface-raised transition duration-200 rounded-sm">
                                    <Filter className="h-4 w-4" />
                                </div>
                            </Popover.Target>

                            <Popover.Dropdown
                                classNames={{
                                    dropdown: "!bg-ds-surface-raised !px-0 !py-0",
                                }}
                            >
                                {frontendFilters}
                            </Popover.Dropdown>
                        </Popover>
                    </div>
                )}

                {widget.title !== null && (
                    <Tooltip
                        label={widget.title}
                        position="top-start"
                    >
                        <span
                            className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                            style={{
                                maxWidth: `${0.8 * widgetWidth}px`,
                            }}
                        >
                            {widget.title}
                        </span>
                    </Tooltip>
                )}

                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={tableRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                            extraOptions={[
                                {
                                    id: "652c3691-3338-4570-ae6e-a7d8bc5cba78" as Uuid,
                                    label: "Download as CSV",
                                    onClick: () => {
                                        if (widget.data === null) {
                                            return;
                                        }

                                        const csvContent = csvStringify(widget.data, {
                                            header: true,
                                        });

                                        // TODO: Move this to a common utility
                                        const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8,"});
                                        const objectUrl = URL.createObjectURL(blob);
                                        const a = document.createElement("a");
                                        a.setAttribute("href", objectUrl);
                                        a.setAttribute("download", "data.csv");
                                        a.click();
                                        // console.log(a);
                                        showSuccessToast("Success", "CSV downloaded");
                                    },
                                },
                            ]}
                        />
                    </div>
                )}
            </div>

            {widget.comparison.enabled !== true && <div className="row-start-3 justify-self-end self-center">{datePicker}</div>}
        </div>
    );
}

function getTableGridTemplateColumns({
    keys,
    columnProperties,
    widgetWidth,
    horizontalPaddingInPx = 2 * paddingInRem * remToPx,
}: {
    keys: Array<string>;
    columnProperties: Record<string, ColumnProperties> | null | undefined;
    widgetWidth: number;
    horizontalPaddingInPx?: number;
}): string {
    if (columnProperties === null || columnProperties === undefined) {
        return `repeat(${keys.length}, minmax(0, 1fr))`;
    }

    const availableWidth = Math.max(widgetWidth - horizontalPaddingInPx, 1);

    return keys
        .map((key) => {
            const columnWidth = columnProperties[key]?.columnWidth;
            if (typeof columnWidth !== "number" || Number.isFinite(columnWidth) === false || columnWidth <= 0) {
                return "minmax(0, 1fr)";
            }

            return `${Math.max(Math.floor((Math.floor(columnWidth) / 100) * availableWidth), 1)}px`;
        })
        .join(" ");
}

function getNextActionableToCompleteTableWidget(
    widget: Widget & {
        type: WidgetType.table;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.selectedMetrics.length < widget.minMetricSelections
          ? "Please select metrics"
          : widget.selectedDimensions.length < widget.minDimensionSelections
            ? "Please select dimensions"
            : "Do something else";
}

export function ProgressBarWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    onFilterButtonClick,
    onFilterSelect,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    onFilterSelect: (items: Array<string>, key: string) => void;
    datePicker: React.ReactNode;
    onFilterButtonClick: () => void;
    hideKebabMenu?: boolean;
}) {
    if (widget.type != WidgetType.progressBar) {
        throw new Error("7426ea3b-d6fa-4884-b6fd-144f53ce9f6a");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.progressValue != null &&
            widget.title != null &&
            widget.targetValue != null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2">
                    <div
                        className="text-center gj-text-caption-400"
                        style={{
                            fontSize: `${(referenceFontSize * scalingFactor) / 2}px`,
                        }}
                    >
                        {widget.title}
                    </div>

                    <VerticalSpacer className="h-2" />

                    <Progress
                        // className="text-center"
                        // style={{
                        //     fontSize: `${referenceFontSize * scalingFactor}px`,
                        // }}
                        classNames={{
                            root: "h-full w-full",
                        }}
                        value={(widget.progressValue / widget.targetValue) * 100}
                    />

                    <VerticalSpacer className="h-2" />

                    <div
                        className="grid grid-flow-col grid-cols-3 gap-x-4 w-full place-items-center"
                        style={{
                            fontSize: `75%`,
                        }}
                    >
                        <div className="col-start-1 justify-self-start">0</div>
                        <div className="col-start-2">{((widget.progressValue / widget.targetValue) * 100).toFixed(2)}%</div>
                        <div className="col-start-3 grid place-items-center justify-self-end">{numberToHumanFriendlyString(widget.targetValue)}</div>
                    </div>
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget || widget.targetValue == null ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <ProgressBarIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteProgressBarWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <ProgressBarIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            {widget.title !== null && (
                <Tooltip
                    label={widget.title}
                    position="top-start"
                >
                    <span
                        className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                        style={{
                            maxWidth: `${0.8 * widgetWidth}px`,
                        }}
                    >
                        {widget.title}
                    </span>
                </Tooltip>
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

function getNextActionableToCompleteProgressBarWidget(
    widget: Widget & {
        type: WidgetType.progressBar;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.selectedMetrics.length < widget.minMetricSelections || widget.selectedMetrics.length > widget.maxMetricSelections
          ? "Please select metrics"
          : "Do something else";
}

export function GaugeWidgetComponent({
    widget,
    widgetHeight,
    widgetWidth,
    className,
    scalingFactor,
    datePicker,
    onFilterButtonClick,
    onFilterSelect,
    hideKebabMenu,
}: {
    widget: Widget;
    widgetHeight: number;
    widgetWidth: number;
    className?: string;
    scalingFactor: number;
    onFilterSelect: (items: Array<string>, key: string) => void;
    datePicker: React.ReactNode;
    onFilterButtonClick: () => void;
    hideKebabMenu?: boolean;
}) {
    if (widget.type != WidgetType.gauge) {
        throw new Error("349f7024-2576-49e8-bff0-281548329eb7");
    }

    const widgetRef = useRef<HTMLDivElement | null>(null);

    return (
        <div
            className={twMerge("", className)}
            style={{
                padding: `${paddingInRem}rem`,
                height: `${widgetHeight}px`,
                width: `${widgetWidth}px`,
                display: "grid",
                gridTemplateRows: `${widgetTopBarHeightInRem * remToPx}px minmax(0, 1fr) ${widgetBottomBarHeightInRem * remToPx}px`,
                gap: `0 ${widgetBottomBarHeightInRem * remToPx}`,
            }}
            ref={widgetRef}
        >
            {widget.progressValue != null &&
            widget.title != null &&
            widget.targetValue != null &&
            widget.loadingState === WidgetLoadingState.loadedSuccessfully ? (
                <div className="grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip row-start-2 place-items-center">
                    <div
                        className="text-center gj-text-caption-400"
                        style={{
                            fontSize: `${(referenceFontSize * scalingFactor) / 2}px`,
                        }}
                    >
                        {widget.title}
                    </div>

                    <VerticalSpacer className="h-2" />

                    <Gauge
                        min={0}
                        max={widget.targetValue}
                        value={widget.progressValue}
                        width={(widgetWidth - (paddingInRem - widgetTopBarHeightInRem - widgetBottomBarHeightInRem) * remToPx) / 2 / scalingFactor}
                    />

                    <VerticalSpacer className="h-4" />

                    {Number.isNaN(widget.targetValue) ? null : (
                        <div
                            className="grid grid-flow-col grid-cols-3 gap-x-4 w-full place-items-center"
                            style={{
                                fontSize: `75%`,
                                width: `${widgetWidth / scalingFactor}px`,
                            }}
                        >
                            <div className="col-start-1 justify-self-start">0</div>
                            <div className="col-start-2">{((widget.progressValue / widget.targetValue) * 100).toFixed(2)}%</div>
                            <div className="col-start-3 grid place-items-center justify-self-end">{numberToHumanFriendlyString(widget.targetValue)}</div>
                        </div>
                    )}
                </div>
            ) : widget.loadingState === WidgetLoadingState.incompleteWidget || widget.targetValue == null ? (
                <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2">
                    <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                        <ProgressBarIncomplete className="w-full h-full" />
                    </div>

                    <div className="text-center row-start-2 col-start-1">{getNextActionableToCompleteGaugeWidget(widget)}</div>
                </div>
            ) : (
                widget.loadingState === WidgetLoadingState.errorAfterLoading && (
                    <div className="h-full w-full grid grid-cols-1 grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] place-items-center row-start-2 could-not-fetch-data">
                        <div className="row-span-full h-full object-contain opacity-10 grid place-items-center col-start-1">
                            <ProgressBarIncomplete className="w-full h-full" />
                        </div>

                        <div className="text-center row-start-2 col-start-1">
                            {widget.errorCode === QueryResolverErrorCodes.noDataSourceFound
                                ? `Please link ${IntegrationTypeDisplayMetadata[widget.metrics[0]?.integrationType].displayName} source to fix this widget`
                                : widget.errorCode === QueryResolverErrorCodes.noDataFound
                                  ? "No data found"
                                  : "Oops! Could not fetch data"}
                        </div>
                    </div>
                )
            )}

            {widget.title !== null && (
                <Tooltip
                    label={widget.title}
                    position="top-start"
                >
                    <span
                        className="text-[0.875rem] font-semibold text-ae-v0--foreground--500 self-start line-clamp-1"
                        style={{
                            maxWidth: `${0.8 * widgetWidth}px`,
                        }}
                    >
                        {widget.title}
                    </span>
                </Tooltip>
            )}

            <div className="row-start-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content]">
                {hideKebabMenu != true && (
                    <div className="col-start-3 grid place-items-center">
                        <ViewWidgetPopover
                            widgetRef={widgetRef}
                            id={null}
                            canvasHeight={`${widgetHeight}`}
                            canvasWidth={`${widgetWidth}`}
                        />
                    </div>
                )}
            </div>

            <div className="row-start-3 justify-self-end self-center">{datePicker}</div>
        </div>
    );
}

function getNextActionableToCompleteGaugeWidget(
    widget: Widget & {
        type: WidgetType.gauge;
    },
) {
    return widget.datasetId === null
        ? "Please select Data Cluster"
        : widget.selectedMetrics.length < widget.minMetricSelections || widget.selectedMetrics.length > widget.maxMetricSelections
          ? "Please select metrics"
          : "Do something else";
}
